<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { onKeyStroke } from '@vueuse/core';
import { useAppDownloadStore } from '@/stores/app-download';
import { APP_STORE_LINKS } from '@/utils/apostatudo/app-download-deep-link';
import { getAssetsUrlForCDN } from '@/utils/template';

// Modal "Baixe o aplicativo" (ENG-495). Mesma casca da roletinha e do Promocode:
// Teleport pro body, bottom-sheet colado no rodapé, slide-up por @keyframes (o
// reset global de transition em main.scss mata `transition`) e a store controla
// a abertura sem navegar de rota.
//
// Todas as medidas internas são em `cqw` contra a LARGURA da folha (393px no
// Figma → 1cqw = 3,93px): a composição inteira encolhe junto num celular mais
// estreito, sem nenhum elemento sair do lugar. O `container-type` fica na folha
// e as unidades nos FILHOS — cqw no próprio container cai na viewport.
const store = useAppDownloadStore();
const { isOpen } = storeToRefs(store);

// `visible` mantém o overlay montado até o slide-down terminar; `sheetOpen`
// dispara a animação.
const visible = ref(false);
const sheetOpen = ref(false);

useBodyScrollLock(visible);

const handImage = getAssetsUrlForCDN('/assets/images/app-download/hand.webp');
const logoImage = getAssetsUrlForCDN(
  '/assets/images/app-download/logo-horizontal-white.webp',
);

const googlePlayIcon = getAssetsUrlForCDN(
  '/assets/images/app-download/google-play.svg',
);

const appStoreIcon = getAssetsUrlForCDN(
  '/assets/images/app-download/app-store.svg',
);

// Textos fixos (só pt-BR): é um cartaz de marketing com números da ficha da
// loja, não copy traduzível — por isso não passam pelo texts.json.
const TEXTS = {
  title: 'Baixe o aplicativo oficial e fique por dentro de todas as novidades!',
  close: 'Fechar',
  rating: '4,7',
  ratingLabel: '7,58 mil avaliações',
  ageLabel: 'Classificação 18 anos',
  downloads: '500 mil+',
  downloadsLabel: 'Downloads',
} as const;

const stores = [
  {
    key: 'googlePlay',
    href: APP_STORE_LINKS.googlePlay,
    icon: googlePlayIcon,
    prefix: 'Baixar no',
    name: 'Google Play',
  },
  {
    key: 'appStore',
    href: APP_STORE_LINKS.appStore,
    icon: appStoreIcon,
    prefix: 'Baixar na',
    name: 'App Store',
  },
] as const;

function close() {
  store.close();
}

function onClosed() {
  visible.value = false;
}

watch(
  isOpen,
  (open) => {
    if (open) {
      visible.value = true;
      nextTick(() => {
        sheetOpen.value = true;
      });
    } else {
      sheetOpen.value = false;
    }
  },
  { immediate: true },
);

onKeyStroke('Escape', () => {
  if (isOpen.value) close();
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="ad-overlay"
      @dragstart.prevent
    >
      <div
        class="ad-backdrop"
        :class="{ 'ad-backdrop--out': !sheetOpen }"
        @click="close"
      />

      <Transition
        appear
        name="ad-slide-up"
        @after-leave="onClosed"
      >
        <div
          v-if="sheetOpen"
          :aria-label="TEXTS.title"
          aria-modal="true"
          class="ad-sheet"
          role="dialog"
        >
          <!-- Camada de fundo: o azul do Figma são dois círculos borrados por
               cima de um retângulo escuro; aqui são gradientes. -->
          <div
            aria-hidden="true"
            class="ad-glow"
          />

          <!-- Arte da mão: recorte puro com alpha, posicionada e girada em CSS
               (a rotação de 19° é do Figma). Sangra pelo pé da folha, que é
               `overflow: clip`. -->
          <img
            alt=""
            aria-hidden="true"
            class="ad-hand"
            draggable="false"
            :src="handImage"
          />

          <!-- Fade preto do rodapé, pra os dados da loja lerem por cima da arte. -->
          <div
            aria-hidden="true"
            class="ad-fade"
          />

          <header class="ad-head">
            <img
              :alt="appName()"
              class="ad-logo"
              draggable="false"
              :src="logoImage"
            />

            <button
              :aria-label="TEXTS.close"
              class="ad-close"
              type="button"
              @click="close"
            >
              <NuxtIcon name="close" />
            </button>
          </header>

          <h2 class="ad-title">{{ TEXTS.title }}</h2>

          <div class="ad-stores">
            <a
              v-for="item in stores"
              :key="item.key"
              class="ad-store"
              :href="item.href"
              rel="noopener noreferrer"
              target="_blank"
            >
              <img
                alt=""
                class="ad-store__icon"
                draggable="false"
                :src="item.icon"
              />
              <span class="ad-store__text">
                <small>{{ item.prefix }}</small>
                <strong>{{ item.name }}</strong>
              </span>
            </a>
          </div>

          <!-- Dados da ficha da loja, como no Figma (estáticos). -->
          <footer class="ad-meta">
            <div class="ad-meta__item">
              <span class="ad-meta__value ad-meta__value--rating">
                {{ TEXTS.rating }}
                <NuxtIcon
                  class="ad-meta__star"
                  name="star"
                />
              </span>
              <span class="ad-meta__label">
                {{ TEXTS.ratingLabel }}
              </span>
            </div>

            <span class="ad-meta__divider" />

            <div class="ad-meta__item">
              <!-- Selo "18" em CSS (o PNG do Figma borrava em tela 2x). -->
              <span
                aria-hidden="true"
                class="ad-meta__age"
              >
                18
              </span>
              <span class="ad-meta__label">
                {{ TEXTS.ageLabel }}
              </span>
            </div>

            <span class="ad-meta__divider" />

            <div class="ad-meta__item">
              <span class="ad-meta__value">
                {{ TEXTS.downloads }}
              </span>
              <span class="ad-meta__label">
                {{ TEXTS.downloadsLabel }}
              </span>
            </div>
          </footer>
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<style>
.ad-slide-up-enter-active {
  animation: ad-slide-up 0.32s cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
  will-change: transform, opacity;
}
.ad-slide-up-leave-active {
  animation: ad-slide-up 0.24s cubic-bezier(0.4, 0, 1, 1) reverse forwards;
  will-change: transform, opacity;
}
@keyframes ad-slide-up {
  0% {
    transform: translateY(100%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>

<style scoped lang="scss">
// Entre o Promocode (z-9997) e a roletinha (z-9999) — nunca aparecem juntos.
// `select-none` + `-webkit-user-drag: none` + `draggable="false"` (este último é
// o que cobre o Firefox): nada aqui é pra selecionar nem arrastar — é um cartaz.
.ad-overlay {
  @apply fixed inset-0 z-[9998] flex flex-col justify-end select-none;

  // Desktop: cartão no centro da tela (como no Figma), não colado no rodapé.
  @media (min-width: 1024px) {
    justify-content: center;
  }

  img {
    -webkit-user-drag: none;
  }
}

.ad-backdrop {
  @apply fixed inset-0;

  background: rgba(0, 0, 0, 0.72);
  animation: ad-fade-in 0.25s ease forwards;
}

.ad-backdrop--out {
  animation: ad-fade-in 0.24s ease reverse forwards;
}

@keyframes ad-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

// A folha: 393 × 498 no Figma. Largura no teto de 393px (a mesma das outras
// folhas) e altura por proporção, pra composição não mudar entre aparelhos.
// `overflow: clip`, não `hidden`: hidden é scroll container e o foco no botão
// de fechar poderia rolar a folha (bug das bordas da roletinha).
.ad-sheet {
  position: relative;
  z-index: 10;
  // Celular: a folha cola nas laterais até 460px (entre 393 e 460 ela cresce
  // junto com a tela, sem respiro). Só a partir daí trava no teto de 393.
  width: 100vw;

  @media (min-width: 460px) {
    width: 393px;
  }
  aspect-ratio: 393 / 498;
  max-height: 100%;
  margin: 0 auto;
  overflow: clip;
  border-radius: 16px 16px 0 0;
  background: #030614;

  @media (min-width: 1024px) {
    border-radius: 16px;
  }
  box-shadow: 0 0 82px rgba(4, 58, 145, 0.9);
  container-type: inline-size;
}

// Dois círculos do Figma (Ellipse 82: 431px em y=182; Ellipse 81: 347px em
// y=233), ambos centrados em x. O de fora é o brilho, o de dentro o azul cheio.
.ad-glow {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(
      circle at 50% 104cqw,
      #1d3fe0 0,
      #1d3fe0 40cqw,
      rgba(29, 63, 224, 0) 44cqw
    ),
    radial-gradient(
      circle at 50% 101cqw,
      rgba(18, 74, 214, 0.75) 0,
      rgba(18, 74, 214, 0.75) 52cqw,
      rgba(18, 74, 214, 0) 60cqw
    ),
    radial-gradient(
      ellipse 100% 60% at 50% 75%,
      #0a1c6c 0,
      rgba(3, 6, 20, 0) 100%
    );
}

// Recorte da mão (900 × 1244). No Figma a mão ocupa ~85% da largura da folha,
// com as pontas dos dedos a ~46% do topo; ancorada pelo TOPO e girada 19°, a
// parte de baixo sai pela borda inferior da folha.
.ad-hand {
  position: absolute;
  top: 47cqw;
  left: 16cqw;
  width: 85cqw;
  transform: rotate(19deg);
  transform-origin: 50% 20%;
  pointer-events: none;
  filter: drop-shadow(0 0 6cqw rgba(84, 156, 255, 0.45));
}

// Rectangle 567 do Figma: transparente → preto, de y=300 até o pé.
.ad-fade {
  position: absolute;
  inset: 76cqw 0 0 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0) 0,
    rgba(0, 0, 0, 0.9) 60%,
    #000 100%
  );
}

.ad-head {
  position: absolute;
  top: 3.3cqw;
  right: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 7.6cqw;
}

.ad-logo {
  display: block;
  width: 35.7cqw;
  height: auto;
}

.ad-close {
  @apply absolute flex items-center justify-center rounded-full text-white;

  top: 0;
  right: 4.6cqw;
  width: 7.6cqw;
  height: 7.6cqw;
  font-size: 2.6cqw;
  background: rgba(255, 255, 255, 0.05);

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
}

// Duas linhas no Figma (20,6px em 345px). Tudo explícito (família, peso,
// espaçamento) pra não herdar nada de h2 global; margens de 4cqw, e não os 24px
// do Figma, pra sobrar folga e a quebra não virar três linhas por diferença de
// renderização da fonte.
.ad-title {
  @apply absolute text-center text-white;

  top: 15cqw;
  right: 4.5cqw;
  left: 4.5cqw;
  margin: 0;
  font-family: theme('fontFamily.sans');
  font-size: 5.25cqw;
  font-weight: 700;
  line-height: 1.29;
  letter-spacing: normal;
  text-wrap: balance;
}

// As duas pílulas (124,5 × 34,4 no Figma), lado a lado com 8px de vão.
.ad-stores {
  position: absolute;
  top: 34.5cqw;
  right: 0;
  left: 0;
  display: flex;
  gap: 2cqw;
  justify-content: center;
}

.ad-store {
  @apply flex items-center justify-center text-white no-underline;

  gap: 1.15cqw;
  width: 31.7cqw;
  height: 8.75cqw;
  border: 1px solid #fff;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.15);

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }

  &__icon {
    display: block;
    width: 4.7cqw;
    height: auto;
  }

  &__text {
    display: flex;
    flex-direction: column;
    line-height: 1;

    small {
      font-size: 1.95cqw;
      font-weight: 400;
    }

    strong {
      font-size: 2.6cqw;
      font-weight: 700;
    }
  }
}

// Rodapé da ficha (288px em y=451 no Figma). Três colunas separadas por fios.
.ad-meta {
  position: absolute;
  right: 0;
  bottom: 3.5cqw;
  left: 0;
  display: flex;
  gap: 5cqw;
  align-items: center;
  justify-content: center;

  &__item {
    display: flex;
    flex-direction: column;
    gap: 0.6cqw;
    align-items: center;
    text-align: center;
  }

  &__value {
    display: inline-flex;
    gap: 0.5cqw;
    align-items: center;
    font-size: 2.65cqw;
    font-weight: 600;
    line-height: 1.4;
    color: #fff;
    white-space: nowrap;

    &--rating {
      color: #fef08a;
    }
  }

  &__star {
    font-size: 2.3cqw;
    line-height: 1;
  }

  &__label {
    font-size: 2.3cqw;
    line-height: 1.3;
    color: #cbd5e1;
    white-space: nowrap;
  }

  &__age {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3.9cqw;
    height: 3.9cqw;
    font-size: 2.2cqw;
    font-weight: 700;
    line-height: 1;
    color: #fff;
    border: 1px solid #fff;
    border-radius: 0.6cqw;
    background: #000;
  }

  &__divider {
    flex: none;
    width: 1px;
    height: 4.5cqw;
    background: rgba(232, 234, 237, 0.6);
  }
}
</style>
